import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { addDays, format, subDays } from 'date-fns';
import { filesize } from 'filesize';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { FiUpload, FiSearch, FiDownload } from 'react-icons/fi';
import pt from 'date-fns/locale/pt';

import { Setor } from 'utils/variables';
import {
  Cargos,
  Blocos,
  Turnos,
  isEleicoesMunicipais,
  Status,
} from 'utils/eleicoes';
import { ClienteItem } from '../@types';
import { Container } from '../styles';
import { Tabs, FilterSection } from './styles';
import Materiais from './Materiais';
import PlanoMidia from './PlanoMidia';

import Upload from 'components/Upload';
import Select from 'components/Select/index';
import Button from 'components/Button';
import SelectDate from 'components/SelectDate';

import api from 'services/api';
import { updateCurrentDate } from 'store/modules/usuario/actions';
import { show } from 'store/modules/dialog/actions';
import { toast } from 'react-toastify';

interface ClienteOption extends ClienteItem {
  value: number;
  label: string;
}
interface BlocoOption {
  value: number;
  label: string;
}
interface SelectOption {
  value: string;
  label: string;
}
export default function Painel() {
  /**
   * REDUX
   */
  const dispatch = useDispatch();
  const { currentDate, emissora, profile } = useSelector(
    (state: any) => state.usuario,
  );
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [activeIndex, setActiveIndex] = useState(
    profile.setor === Setor.PROGRAMACAO ? 2 : 1,
  );
  // const [isBloco, setIsBloco] = useState(profile.setor === Setor.PROGRAMACAO);
  const [clientes, setClientes] = useState<ClienteOption[]>([]);
  const [cargo, setCargo] = useState('');
  const [clienteIdFilter, setClienteIdFilter] = useState(0);
  const [blocoFilter, setBlocoFilter] = useState(0);
  const [turnoFilter, setTurnoFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isFilter, setIsFilter] = useState(true);
  const [loadingDownload, setLoadingDownload] = useState(false);

  /**
   * USE REF
   */
  const filterFormRef = useRef<FormHandles>(null);

  /**
   * USE MEMO
   */
  const dateFormatted = useMemo(() => {
    if (currentDate) {
      return format(new Date(currentDate), "d 'de' MMMM", { locale: pt });
    }
  }, [currentDate]);

  const download = useCallback(async (response, filename) => {
    const elink = document.createElement('a');
    const binaryData: any[] = [];
    binaryData.push(response);
    const virtualUrl = window.URL.createObjectURL(response.data);
    elink.href = virtualUrl;
    elink.download = filename;
    elink.click();
  }, []);

  function getColor(option: string) {
    let color;
    switch (option) {
      case 'autorizado':
        color = '#000000 !important';
        break;
      case 'recebido':
        color = '#56CA56 !important';
        break;
      case 'recusado':
        color = '#0055FF !important';
        break;
      case 'atrasado':
        color = '#FFC107 !important';
        break;
      case 'alterado':
        color = '#ba54f5 !important';
        break;
      default:
        color = '#f5365c !important';
        break;
    }
    return color;
  }
  /**
   * USE CALLBACK
   */
  const handlePrevDay = useCallback(() => {
    dispatch(updateCurrentDate(subDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  const handleNextDay = useCallback(() => {
    dispatch(updateCurrentDate(addDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  const handleUpload = useCallback((files: any[]) => {
    const filesFormatted = files.map(file => ({
      ...file,
      file,
      id: uuid(),
      name: file.name,
      readableSize: filesize(file.size),
      progress: 0,
      uploaded: false,
      error: false,
    }));
    setSelectedFiles(filesFormatted);
  }, []);

  const handleDateChange = useCallback(
    date => {
      date && dispatch(updateCurrentDate(date));
    },
    [dispatch],
  );

  const handleDownloadRelatorio = useCallback(async () => {
    setLoadingDownload(true);
    try {
      const response = await api.get('/eleicoes/insercao/relatoriopainel', {
        timeout: 360000,
        headers: { 'Content-Type': 'application/octet-stream' },
        responseType: 'blob',
        params: {
          emissora: emissora.value,
          data: format(new Date(currentDate), 'yyyy-MM-dd'),
        },
      });
      if (response) await download(response, 'Painel Eleições ADX.xlsx');
      else toast.error('Falha de comunicação com o servidor');
    } catch (error) {
      toast.error('Falha ao baixar o arquivo. Tente novamente mais tarde');
    } finally {
      setLoadingDownload(false);
    }
  }, [currentDate, download, emissora.value]);

  const updateFile = useCallback(
    (id, data) => {
      const files = selectedFiles.map(uploadedFile => {
        return id === uploadedFile.id
          ? { ...uploadedFile, ...data }
          : uploadedFile;
      });
      setSelectedFiles(files);
    },
    [selectedFiles],
  );

  const handleSuccessMessage = useCallback(() => {
    const option = {
      type: 'success',
      confirmButtonText: '',
      contentHTML: 'Planilha carregada',
      timer: 3000,
      cancelButtonText: 'NÃO',
      showCancelButton: false,
      showConfirmButton: false,
      showCloseButton: true,
    };
    dispatch(show(option));
  }, [dispatch]);

  const handleErrorMessage = useCallback(
    message => {
      const option = {
        type: 'error',
        confirmButtonText: '',
        text: message,
        timer: 3000,
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      };
      dispatch(show(option));
    },
    [dispatch],
  );

  const processUpload = useCallback(
    selectedFile => {
      const data = new FormData();

      data.append('file', selectedFile.file);
      data.append('emissora_id', emissora.value);
      data.append('tipo', activeIndex === 2 ? 'bloco' : 'insercao');
      const route =
        activeIndex !== 3
          ? `/eleicoes/planodemidia`
          : `/eleicoes/planilhaaprovados`;

      api
        .post(route, data, {
          onUploadProgress: function(progressEvent) {
            const progress = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;

            updateFile(selectedFile.id, {
              progress,
            });
          },
        })
        .then(() => {
          updateFile(selectedFile.id, {
            uploaded: true,
          });
          setTimeout(() => setSelectedFiles([]), 2000);
          handleSuccessMessage();
        })
        .catch(err => {
          updateFile(selectedFile.id, {
            error: true,
          });
          setTimeout(() => setSelectedFiles([]), 2000);
          handleErrorMessage(err.message);
        });
    },
    [
      emissora.value,
      activeIndex,
      updateFile,
      handleSuccessMessage,
      handleErrorMessage,
    ],
  );

  useEffect(
    () =>
      selectedFiles.forEach(selectedFile => {
        if (
          !selectedFile.uploaded &&
          !selectedFile.error &&
          selectedFile.progress === 0
        ) {
          processUpload(selectedFile);
        }
      }),
    [processUpload, selectedFiles],
  );
  useEffect(() => {
    api
      .get<ClienteItem[]>('/clientes', { params: { politico: true } })
      .then(response => {
        setClientes(
          response.data.map(c => ({ value: c.id, label: c.mnemonico, ...c })),
        );
      })
      .catch(error => console.log(error))
      .finally();
  }, []);

  const renderContent = useCallback(() => {
    switch (activeIndex) {
      case 1:
        return (
          <PlanoMidia
            clienteId={clienteIdFilter}
            blocoFilter={blocoFilter}
            isBloco={false}
            turnoFilter={turnoFilter}
            cargo={cargo}
            statusFilter={statusFilter}
          />
        );
      case 2:
        return (
          <PlanoMidia
            clienteId={clienteIdFilter}
            blocoFilter={blocoFilter}
            turnoFilter={turnoFilter}
            isBloco
            cargo={cargo}
            statusFilter={statusFilter}
          />
        );
      case 3:
        return <Materiais clienteId={clienteIdFilter} />;
      default:
        return <Materiais />;
    }
  }, [
    activeIndex,
    blocoFilter,
    cargo,
    clienteIdFilter,
    statusFilter,
    turnoFilter,
  ]);

  return (
    <Container>
      <header>
        <section>
          <h6>PAINEL OPERACIONAL</h6>
          <Tabs $activeIndex={activeIndex}>
            <button
              onClick={() => {
                if (filterFormRef.current)
                  filterFormRef.current.clearField('turno');
                setActiveIndex(1);
              }}
            >
              Inserções
            </button>
            <button
              onClick={() => {
                if (filterFormRef.current)
                  filterFormRef.current.clearField('bloco');
                setActiveIndex(2);
              }}
            >
              Bloco
            </button>
            <button
              onClick={() => {
                setActiveIndex(3);
              }}
            >
              Materiais
            </button>
          </Tabs>
        </section>

        {dateFormatted && currentDate && (
          <SelectDate
            dateFormatted={dateFormatted}
            currentDateZoned={String(new Date(currentDate))}
            handleNextDay={handleNextDay}
            handleDateChange={handleDateChange}
            handlePrevDay={handlePrevDay}
            // disabledNextDay={getDate(currentDateZoned) === getDate(new Date())}
          />
        )}
        <FilterSection $isFilter={isFilter}>
          <Form ref={filterFormRef} onSubmit={() => {}}>
            {isFilter && (
              <div className="upperFilter">
                {activeIndex === 2 ? (
                  <Select
                    name="turno"
                    menuPortalTarget={document.body}
                    searchable
                    type="default"
                    options={Turnos}
                    placeholder="Selecione o turno"
                    isClearable
                    onChange={(selected: SelectOption) =>
                      selected
                        ? setTurnoFilter(selected.value)
                        : setTurnoFilter('')
                    }
                  />
                ) : (
                  <Select
                    name="bloco"
                    menuPortalTarget={document.body}
                    searchable
                    type="default"
                    options={Blocos}
                    placeholder="Selecione o bloco"
                    isClearable
                    onChange={(selected: BlocoOption) =>
                      selected
                        ? setBlocoFilter(selected.value)
                        : setBlocoFilter(0)
                    }
                    disabled={activeIndex === 3}
                  />
                )}
                <Select
                  name="cargo"
                  menuPortalTarget={document.body}
                  isSearchable={false}
                  type="default"
                  options={Cargos.filter(
                    c => c.isEleicoesMunicipais === isEleicoesMunicipais,
                  )}
                  defaultValue={Cargos.find(c => c.value === cargo)}
                  placeholder="Selecione o cargo"
                  isClearable
                  onChange={(selected: SelectOption) =>
                    selected ? setCargo(selected.value) : setCargo('')
                  }
                  disabled={activeIndex === 3}
                />
              </div>
            )}
            <div className="optionButton">
              {isFilter ? (
                <>
                  <Select
                    name="partido"
                    width="367px"
                    menuPortalTarget={document.body}
                    searchable
                    type="default"
                    options={clientes}
                    defaultValue={clientes.find(
                      cli => cli.id === clienteIdFilter,
                    )}
                    placeholder="Selecione o partido"
                    isClearable
                    onChange={(selected: ClienteOption) =>
                      selected
                        ? setClienteIdFilter(selected.value)
                        : setClienteIdFilter(0)
                    }
                  />
                  <Select
                    className="filtroStatus"
                    name="status"
                    searchable
                    options={Status}
                    placeholder="●"
                    isClearable
                    styles={{
                      singleValue: (styles: any, state: any) => ({
                        ...styles,
                        color: getColor(state.data.value),
                      }),
                      option: (styles: any, state: any) => ({
                        ...styles,
                        color: getColor(state.value),
                      }),
                    }}
                    onChange={(selected: SelectOption) =>
                      selected
                        ? setStatusFilter(selected.value)
                        : setStatusFilter('')
                    }
                  />
                  <Button
                    id="filter"
                    fill
                    color="font"
                    round
                    onClick={() => setIsFilter(!isFilter)}
                  >
                    <FiUpload />
                  </Button>
                </>
              ) : (
                <div>
                  <div>
                    <Upload
                      name={
                        activeIndex !== 3
                          ? 'Upload Planilha Compulsório'
                          : 'Upload Aprovados'
                      }
                      onUpload={handleUpload}
                      disabled={false}
                      accept={{
                        'application/vnd.ms-excel': ['.xls'],
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
                          '.xlsx',
                        ],
                      }}
                      selectedFiles={selectedFiles}
                    />
                    <div className="painelButton">
                      <Button
                        color="primary"
                        onClick={() => handleDownloadRelatorio()}
                      >
                        {loadingDownload ? (
                          'Gerando arquivo...'
                        ) : (
                          <>
                            Exportar Painel
                            <FiDownload />
                          </>
                        )}
                      </Button>
                      <Button
                        id="filter"
                        fill
                        color="font"
                        round
                        onClick={() => setIsFilter(!isFilter)}
                      >
                        <FiSearch />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Form>
        </FilterSection>
      </header>
      {renderContent()}
    </Container>
  );
}
